import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';
import { AlertTriangle } from 'lucide-react';

export default function OutputDisclaimer() {
  return (
    <div className="min-h-screen bg-surface">
      <nav className="border-b border-outline-variant/20 bg-surface/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo className="h-9 text-primary" />
          </Link>
          <Link to="/auth" className="text-xs font-bold uppercase tracking-widest text-primary hover:opacity-80 transition-opacity">Open Calculator</Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-4">Legal</p>
        <h1 className="text-4xl font-headline font-bold text-primary mb-4">Output Disclaimer</h1>
        <p className="text-sm text-on-surface-variant mb-12">This disclaimer applies to all outputs, reports, exports, and results generated by Site Yield.</p>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-10 flex items-start gap-4">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-bold text-amber-900 mb-2">Indicative scenario output only</p>
            <p className="text-sm text-amber-800 leading-relaxed">
              Every output from this tool is an indicative scenario estimate only. Results depend on your inputs, assumptions, market conditions, planning controls, construction costs, finance availability, taxes, consultant advice, and other variables that may change without notice. Do not use any output as the sole or primary basis for acquisition, pricing, lending, investment, or development decisions.
            </p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none space-y-8 font-body text-on-surface">

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">What outputs represent</h2>
            <p className="leading-relaxed text-on-surface-variant">
              Outputs generated by this tool are indicative scenario estimates only. They are not valuations, appraisals, financial projections, lender reports, planning assessments, tax advice, or any other form of professional advice. They represent the mathematical result of applying your stated inputs and the tool's default assumptions to a simplified model.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">What outputs do not represent</h2>
            <ul className="list-disc pl-6 space-y-2 text-on-surface-variant">
              <li>A guarantee of any financial return, margin, yield, or profit.</li>
              <li>A representation that a site is suitable for development or acquisition.</li>
              <li>A determination of planning or development approval likelihood.</li>
              <li>A lender-ready, bank-accepted, or investment-grade analysis.</li>
              <li>A substitute for a formal feasibility study by a qualified professional.</li>
              <li>Legal, financial, tax, valuation, planning, engineering, or construction advice.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">Key assumption dependencies</h2>
            <p className="leading-relaxed text-on-surface-variant">
              All outputs depend materially on the assumptions used. Key assumptions that affect outputs include: land acquisition cost, construction cost benchmarks, finance rate and holding costs, end values and sales rates, planning yield and unit count, approval timing, stamp duty treatment, GST treatment, contingency and consultant fee allowances, and demolition and civil cost allowances. Any of these may differ significantly from actual project conditions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">Data currency</h2>
            <p className="leading-relaxed text-on-surface-variant">
              Data used in generating outputs — including GIS zoning data and comparable sales information — is sourced from third parties and updated periodically. Currency of data is not guaranteed. Always verify planning controls with the relevant planning authority and obtain current sales evidence from a licensed valuer or agent before relying on any output.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">Required professional verification</h2>
            <p className="leading-relaxed text-on-surface-variant mb-3">
              Before acting on any output, you must independently verify assumptions and obtain professional advice, including from:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-on-surface-variant">
              <li><strong>Town planner</strong> — to confirm planning controls, zoning, permissible uses, and yield.</li>
              <li><strong>Quantity surveyor or builder</strong> — to confirm construction cost assumptions.</li>
              <li><strong>Licensed valuer or local agent</strong> — to confirm end value and sales evidence assumptions.</li>
              <li><strong>Mortgage broker or lender</strong> — to confirm finance availability, rates, and loan terms.</li>
              <li><strong>Accountant or tax adviser</strong> — to confirm stamp duty, GST, income tax, and other tax obligations.</li>
              <li><strong>Property lawyer</strong> — to confirm title, contract, and transactional matters.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-3">Exported reports and PDFs</h2>
            <p className="leading-relaxed text-on-surface-variant">
              Any report or PDF exported from this tool carries this disclaimer by reference. Exported reports are for general information and internal scenario discussion purposes only. They must not be presented to lenders, investors, partners, or third parties as a formal feasibility analysis, valuation, or investment recommendation without independent professional verification and appropriate disclosures.
            </p>
            <p className="leading-relaxed text-on-surface-variant mt-3 font-bold">
              All exported outputs should be treated as: "Indicative scenario only — not verified by independent professionals — not for sole reliance."
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t border-outline-variant/20 py-8 px-6">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-4 text-xs text-on-surface-variant">
          <Link to="/legal/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/legal/terms-of-use" className="hover:text-primary transition-colors">Terms of Use</Link>
          <Link to="/legal/cookie-notice" className="hover:text-primary transition-colors">Cookie Notice</Link>
          <Link to="/legal/output-disclaimer" className="hover:text-primary transition-colors font-bold text-primary">Output Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}
